// src/pages/documentation/gestion/GestionVentas.jsx

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faUpload,
    faFile,
    faShoppingCart,
    faRobot,
    faCog,
    faFilter,
    faDownload,
    faSync,
    faCheckCircle,
    faExclamationTriangle,
    faArrowRight,
    faArrowLeft,
    faInfoCircle,
    faLightbulb,
    faClock
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

const GestionVentas = () => {
    const [expandedSections, setExpandedSections] = useState({
        introduccion: true,
        subirArchivo: true,
        mapeo: true,
        procesar: true,
        verVentas: true
    });

    const toggleSection = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    return (
        <DocumentationLayout>
            <div className="p-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 bg-orange-600 rounded-lg">
                            <FontAwesomeIcon icon={faChartLine} className="text-white text-2xl" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">Gestión de Ventas</h1>
                            <p className="text-gray-600">Importa, procesa y analiza tus ventas desde archivos CSV o Excel</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Link to="/documentation" className="hover:text-gray-700">Centro de Ayuda</Link>
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                        <Link to="/documentation/gestion" className="hover:text-gray-700">Gestión</Link>
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                        <span className="text-gray-700">Ventas</span>
                    </div>
                </div>

                {/* Introducción */}
                <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-xl p-6 mb-8">
                    <div className="flex items-start gap-4">
                        <FontAwesomeIcon icon={faInfoCircle} className="text-orange-600 text-xl mt-1" />
                        <div>
                            <h3 className="font-semibold text-gray-900 mb-2">¿Qué es la Gestión de Ventas?</h3>
                            <p className="text-gray-700 mb-3">
                                La gestión de ventas te permite importar ventas desde archivos CSV o Excel de diferentes sistemas de punto de venta 
                                (Shopify, Square, SumUp, etc.), procesarlas automáticamente con inteligencia artificial y analizar tus datos de ventas 
                                en un solo lugar.
                            </p>
                            <div className="bg-white rounded-lg p-4 border border-orange-100 mt-4">
                                <h4 className="font-semibold text-orange-800 mb-2 flex items-center gap-2">
                                    <FontAwesomeIcon icon={faRobot} />
                                    Procesamiento Automático Inteligente
                                </h4>
                                <p className="text-orange-700 text-sm mb-2">
                                    El sistema procesa todo automáticamente en segundo plano:
                                </p>
                                <ul className="text-orange-700 text-sm space-y-1">
                                    <li>✅ Subes el archivo → Respuesta inmediata</li>
                                    <li>✅ Validación automática → Verifica formato y estructura</li>
                                    <li>✅ Mapeo con IA → Reconoce campos automáticamente</li>
                                    <li>✅ Procesamiento → Importa todas las ventas</li>
                                    <li>✅ Notificación → Te avisamos cuando termina</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sección: Subir Archivo */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
                    <button
                        onClick={() => toggleSection('subirArchivo')}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-10 h-10 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold">
                                <FontAwesomeIcon icon={faUpload} />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-gray-800">Subir Archivo de Ventas</h2>
                                <p className="text-sm text-gray-500">Importa ventas desde CSV o Excel</p>
                            </div>
                        </div>
                        <FontAwesomeIcon
                            icon={faArrowRight}
                            className={`text-gray-400 transform transition-transform ${expandedSections.subirArchivo ? 'rotate-90' : ''}`}
                        />
                    </button>

                    {expandedSections.subirArchivo && (
                        <div className="px-6 pb-6 border-t border-gray-100 bg-gray-50">
                            <div className="pt-6 space-y-6">
                                {/* Subir Archivo */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faUpload} className="text-orange-600" />
                                        1. Subir un Archivo de Ventas
                                    </h3>
                                    <div className="space-y-4">
                                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-orange-800 mb-2">📍 ¿Dónde subir archivos?</h4>
                                            <p className="text-orange-700 text-sm mb-3">
                                                Ve a la página de <strong>"Gestión de Ventas"</strong> desde tu dashboard. 
                                                En la pestaña <strong>"Subir Archivo"</strong>, arrastra tu archivo o haz clic para seleccionarlo.
                                            </p>
                                        </div>

                                        <div className="space-y-3">
                                            <h4 className="font-semibold text-gray-700">Requisitos del archivo:</h4>
                                            <ul className="space-y-2 text-sm text-gray-600">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Formatos aceptados:</strong> CSV o Excel (.xlsx, .xls)
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Tamaño máximo:</strong> 10MB
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Columnas requeridas:</strong> Al menos fecha y monto (el sistema puede reconocer variaciones automáticamente)
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">Sistemas compatibles:</h4>
                                            <ul className="text-blue-700 text-sm space-y-1">
                                                <li>• Shopify</li>
                                                <li>• Square</li>
                                                <li>• SumUp</li>
                                                <li>• Cualquier sistema que exporte CSV/Excel</li>
                                            </ul>
                                        </div>

                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-green-800 mb-2">Información a seleccionar:</h4>
                                            <ul className="text-green-700 text-sm space-y-2">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Marca:</strong> Selecciona a qué marca pertenecen estas ventas</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Tiendas:</strong> Selecciona las tiendas donde se realizaron las ventas (puedes seleccionar múltiples)</div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                                                <FontAwesomeIcon icon={faLightbulb} />
                                                Tip
                                            </h4>
                                            <p className="text-yellow-700 text-sm">
                                                Puedes subir múltiples archivos. El sistema procesará cada uno por separado y te notificará 
                                                cuando cada uno esté listo.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Drag & Drop */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faFile} className="text-orange-600" />
                                        2. Usar Drag & Drop
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            La forma más fácil de subir archivos es arrastrándolos directamente a la zona de carga.
                                        </p>
                                        
                                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-orange-800 mb-2">Cómo usar drag & drop:</h4>
                                            <ol className="text-orange-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>Abre la carpeta donde tienes tu archivo CSV o Excel</li>
                                                <li>Arrastra el archivo directamente a la zona de carga en la página</li>
                                                <li>El archivo se seleccionará automáticamente</li>
                                                <li>Selecciona marca y tiendas</li>
                                                <li>Haz clic en "Subir Archivo"</li>
                                            </ol>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Sección: Mapeo */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
                    <button
                        onClick={() => toggleSection('mapeo')}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                                <FontAwesomeIcon icon={faRobot} />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-gray-800">Mapeo de Campos</h2>
                                <p className="text-sm text-gray-500">Mapeo automático con IA o manual</p>
                            </div>
                        </div>
                        <FontAwesomeIcon
                            icon={faArrowRight}
                            className={`text-gray-400 transform transition-transform ${expandedSections.mapeo ? 'rotate-90' : ''}`}
                        />
                    </button>

                    {expandedSections.mapeo && (
                        <div className="px-6 pb-6 border-t border-gray-100 bg-gray-50">
                            <div className="pt-6 space-y-6">
                                {/* Mapeo Automático */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faRobot} className="text-blue-600" />
                                        1. Mapeo Automático con IA (Recomendado)
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            El sistema usa inteligencia artificial para reconocer automáticamente los campos de tu archivo.
                                        </p>
                                        
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">Cómo funciona:</h4>
                                            <ol className="text-blue-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>Después de subir el archivo, el sistema lo valida automáticamente</li>
                                                <li>La IA analiza las columnas y reconoce campos comunes (fecha, monto, cliente, etc.)</li>
                                                <li>El mapeo se realiza automáticamente en segundo plano</li>
                                                <li>Recibirás una notificación cuando el mapeo esté completo</li>
                                                <li>Si la IA no puede reconocer algunos campos, se te pedirá mapeo manual</li>
                                            </ol>
                                        </div>

                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-green-800 mb-2">✅ Ventajas:</h4>
                                            <ul className="text-green-700 text-sm space-y-1">
                                                <li>• Proceso completamente automático</li>
                                                <li>• Ahorra tiempo y reduce errores</li>
                                                <li>• Reconoce múltiples formatos y variaciones</li>
                                                <li>• Funciona con la mayoría de sistemas de punto de venta</li>
                                            </ul>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                                                <FontAwesomeIcon icon={faClock} />
                                                Tiempo de procesamiento
                                            </h4>
                                            <p className="text-yellow-700 text-sm">
                                                El mapeo automático puede tardar entre 1-3 minutos dependiendo del tamaño del archivo. 
                                                Recibirás una notificación cuando esté listo.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Mapeo Manual */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faCog} className="text-blue-600" />
                                        2. Mapeo Manual (Si es necesario)
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Si la IA no puede reconocer algunos campos, o si prefieres hacerlo manualmente, puedes mapear los campos tú mismo.
                                        </p>
                                        
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">Cuándo usar mapeo manual:</h4>
                                            <ul className="text-blue-700 text-sm space-y-2">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>La IA no puede reconocer algunos campos específicos</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Tienes campos personalizados que no son estándar</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Prefieres tener control total sobre el mapeo</div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-purple-800 mb-2">Cómo hacer mapeo manual:</h4>
                                            <ol className="text-purple-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>En la pestaña "Archivos Subidos", encuentra el archivo que necesita mapeo</li>
                                                <li>Haz clic en el botón <strong>"Manual"</strong></li>
                                                <li>Se abrirá una interfaz visual con las columnas de tu archivo</li>
                                                <li>Para cada columna, selecciona a qué campo corresponde (fecha, monto, cliente, etc.)</li>
                                                <li>Configura formatos si es necesario (separadores decimales, formato de fecha/hora)</li>
                                                <li>Guarda el mapeo</li>
                                            </ol>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-yellow-800 mb-2">Campos que puedes mapear:</h4>
                                            <ul className="text-yellow-700 text-sm space-y-1">
                                                <li>• Fecha y hora de la venta</li>
                                                <li>• Monto total</li>
                                                <li>• Cliente (email, teléfono, nombre)</li>
                                                <li>• Productos comprados</li>
                                                <li>• Método de pago</li>
                                                <li>• Descuentos aplicados</li>
                                                <li>• Y más...</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Sección: Procesar */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
                    <button
                        onClick={() => toggleSection('procesar')}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                                <FontAwesomeIcon icon={faSync} />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-gray-800">Procesar Ventas</h2>
                                <p className="text-sm text-gray-500">Importa y procesa las ventas del archivo</p>
                            </div>
                        </div>
                        <FontAwesomeIcon
                            icon={faArrowRight}
                            className={`text-gray-400 transform transition-transform ${expandedSections.procesar ? 'rotate-90' : ''}`}
                        />
                    </button>

                    {expandedSections.procesar && (
                        <div className="px-6 pb-6 border-t border-gray-100 bg-gray-50">
                            <div className="pt-6 space-y-6">
                                {/* Procesamiento Automático */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faSync} className="text-green-600" />
                                        1. Procesamiento Automático
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Una vez que el archivo está mapeado (automáticamente o manualmente), el sistema puede procesar 
                                            las ventas automáticamente en segundo plano.
                                        </p>
                                        
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-green-800 mb-2">Qué hace el procesamiento automático:</h4>
                                            <ul className="text-green-700 text-sm space-y-2">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Importa ventas:</strong> Crea registros de todas las ventas del archivo</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Detecta duplicados:</strong> Evita importar ventas que ya existen</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Crea productos:</strong> Si hay productos nuevos, los crea automáticamente</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Enriquece tickets:</strong> Vincula ventas con tickets de clientes si es posible</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Notifica:</strong> Te avisa cuando el procesamiento está completo</div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">Cómo iniciar procesamiento automático:</h4>
                                            <ol className="text-blue-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>Si el mapeo fue automático, el procesamiento puede iniciarse automáticamente</li>
                                                <li>Si necesitas iniciarlo manualmente, ve a "Archivos Subidos"</li>
                                                <li>Haz clic en <strong>"Procesar"</strong> en el archivo mapeado</li>
                                                <li>El sistema comenzará a procesar en segundo plano</li>
                                                <li>Recibirás una notificación cuando termine</li>
                                            </ol>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                                                <FontAwesomeIcon icon={faClock} />
                                                Tiempo de procesamiento
                                            </h4>
                                            <p className="text-yellow-700 text-sm">
                                                El tiempo depende del número de ventas. Archivos con miles de ventas pueden tardar varios minutos. 
                                                El sistema procesa en lotes optimizados para manejar grandes volúmenes eficientemente.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Notificaciones */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
                                        2. Centro de Notificaciones
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            El sistema te mantiene informado del progreso mediante notificaciones en tiempo real.
                                        </p>
                                        
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-green-800 mb-2">Tipos de notificaciones:</h4>
                                            <ul className="text-green-700 text-sm space-y-2">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Archivo subido:</strong> Confirmación de que el archivo se subió correctamente</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Mapeo completado:</strong> Cuando el mapeo automático o manual está listo</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Procesamiento completado:</strong> Cuando todas las ventas se han importado</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Errores:</strong> Si hay problemas que requieren tu atención</div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">Cómo ver notificaciones:</h4>
                                            <p className="text-blue-700 text-sm">
                                                En la página de Gestión de Ventas, verás un <strong>Centro de Notificaciones</strong> 
                                                en la parte superior. Las notificaciones aparecen automáticamente y puedes cerrarlas 
                                                cuando las hayas leído.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Sección: Ver Ventas */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
                    <button
                        onClick={() => toggleSection('verVentas')}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">
                                <FontAwesomeIcon icon={faShoppingCart} />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-gray-800">Ver y Analizar Ventas</h2>
                                <p className="text-sm text-gray-500">Consulta, filtra y exporta tus ventas</p>
                            </div>
                        </div>
                        <FontAwesomeIcon
                            icon={faArrowRight}
                            className={`text-gray-400 transform transition-transform ${expandedSections.verVentas ? 'rotate-90' : ''}`}
                        />
                    </button>

                    {expandedSections.verVentas && (
                        <div className="px-6 pb-6 border-t border-gray-100 bg-gray-50">
                            <div className="pt-6 space-y-6">
                                {/* Ver Ventas */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faShoppingCart} className="text-indigo-600" />
                                        1. Ver Ventas Procesadas
                                    </h3>
                                    <div className="space-y-4">
                                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-indigo-800 mb-2">📍 ¿Dónde ver las ventas?</h4>
                                            <p className="text-indigo-700 text-sm mb-3">
                                                En la página de Gestión de Ventas, ve a la pestaña <strong>"Ver Ventas"</strong>. 
                                                Aquí verás todas las ventas procesadas en una tabla.
                                            </p>
                                        </div>

                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">Información que verás:</h4>
                                            <ul className="text-blue-700 text-sm space-y-2">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Fecha y hora de la venta</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Monto total</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Cliente (si está vinculado)</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Productos comprados</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Tienda donde se realizó la venta</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Método de pago</div>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                {/* Filtrar Ventas */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faFilter} className="text-indigo-600" />
                                        2. Filtrar Ventas
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Puedes filtrar las ventas para encontrar información específica rápidamente.
                                        </p>
                                        
                                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-indigo-800 mb-2">Filtros disponibles:</h4>
                                            <ul className="text-indigo-700 text-sm space-y-2">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Por fecha:</strong> Rango de fechas específico</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Por tienda:</strong> Ventas de una tienda específica</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Por cliente:</strong> Ventas de un cliente específico</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Por monto:</strong> Rango de montos</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Por método de pago:</strong> Filtrar por tipo de pago</div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                                                <FontAwesomeIcon icon={faLightbulb} />
                                                Tip
                                            </h4>
                                            <p className="text-yellow-700 text-sm">
                                                Puedes combinar múltiples filtros para análisis más específicos. 
                                                Por ejemplo, ver ventas de una tienda específica en un rango de fechas.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Estadísticas */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faChartLine} className="text-indigo-600" />
                                        3. Estadísticas de Ventas
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            En la parte superior de la página de "Ver Ventas" verás estadísticas resumidas.
                                        </p>
                                        
                                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-indigo-800 mb-2">Estadísticas disponibles:</h4>
                                            <ul className="text-indigo-700 text-sm space-y-2">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Total de ventas:</strong> Número total de ventas procesadas</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Monto total:</strong> Suma de todos los montos de ventas</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Ventas de hoy:</strong> Número de ventas realizadas hoy</div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <p className="text-green-700 text-sm">
                                                Las estadísticas se actualizan automáticamente cuando procesas nuevos archivos o cuando 
                                                se aplican filtros a la tabla de ventas.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Exportar Ventas */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faDownload} className="text-indigo-600" />
                                        4. Exportar Ventas
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Puedes exportar las ventas filtradas a un archivo CSV para análisis externos o reportes.
                                        </p>
                                        
                                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-indigo-800 mb-2">Cómo exportar:</h4>
                                            <ol className="text-indigo-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>En la pestaña "Ver Ventas", aplica los filtros que desees (opcional)</li>
                                                <li>Haz clic en el botón <strong>"Exportar a CSV"</strong></li>
                                                <li>Se descargará un archivo CSV con todas las ventas visibles (filtradas o no)</li>
                                                <li>Abre el archivo en Excel o cualquier programa de hojas de cálculo</li>
                                            </ol>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                                                <FontAwesomeIcon icon={faLightbulb} />
                                                Tip
                                            </h4>
                                            <p className="text-yellow-700 text-sm">
                                                Si aplicas filtros antes de exportar, solo se exportarán las ventas que cumplan esos filtros. 
                                                Esto es útil para crear reportes específicos por período, tienda, etc.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Resumen del Flujo */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Flujo Completo de Trabajo</h3>
                    <div className="space-y-4">
                        <div className="bg-white rounded-lg p-4 border border-blue-100">
                            <h4 className="font-semibold text-gray-800 mb-2">Escenario Recomendado (Automático):</h4>
                            <ol className="text-gray-700 text-sm space-y-2 list-decimal list-inside">
                                <li>Sube tu archivo CSV/Excel arrastrándolo a la zona de carga</li>
                                <li>Selecciona marca y tiendas</li>
                                <li>Haz clic en "Subir Archivo"</li>
                                <li>El sistema procesa automáticamente en segundo plano</li>
                                <li>Recibes notificación cuando está listo</li>
                                <li>Las ventas aparecen automáticamente en "Ver Ventas"</li>
                            </ol>
                            <p className="text-green-700 text-sm mt-3 font-medium">
                                ✅ Todo automático - Solo necesitas 1 click después de subir el archivo
                            </p>
                        </div>

                        <div className="bg-white rounded-lg p-4 border border-blue-100">
                            <h4 className="font-semibold text-gray-800 mb-2">Si Necesitas Mapeo Manual:</h4>
                            <ol className="text-gray-700 text-sm space-y-2 list-decimal list-inside">
                                <li>Sube el archivo (igual que antes)</li>
                                <li>Si la IA no puede mapear algunos campos, recibirás una notificación</li>
                                <li>Ve a "Archivos Subidos" y haz clic en "Manual"</li>
                                <li>Completa los campos faltantes en la interfaz visual</li>
                                <li>Guarda el mapeo y haz clic en "Procesar"</li>
                                <li>Recibirás notificación cuando termine</li>
                            </ol>
                        </div>
                    </div>
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                    <Link
                        to="/documentation/gestion"
                        className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900 font-medium"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                        Volver a Gestión
                    </Link>
                    <div className="text-sm text-gray-500">
                        <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mr-2" />
                        Guía completa
                    </div>
                </div>
            </div>
        </DocumentationLayout>
    );
};

export default GestionVentas;

